// Live USD→NZD display rate for the portal (TASK-380). Billing is deliberately
// USD-only end-to-end (see documentation/credits/ — no FX in the engine); NZD is
// a display-only overlay, so a fetched reference rate only ever changes what
// operators SEE, never what anyone is charged.
//
// Source: Frankfurter (https://frankfurter.dev) — ECB daily reference rates.
// Keyless, CORS `*`. RBNZ has no programmatic access (WAF blocks non-browser
// clients, no CORS), so the ECB reference is the trusted public option.

/** Offline fallback — the documented NZD-anchored default ($0.30 ≈ NZD $0.50 @ 1.69). */
export const DEFAULT_FX_NZD = 1.69;

const CACHE_KEY = 'numa-csp:fx-usd-nzd:v1';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // ECB publishes once per business day

export interface FxRate {
  /** NZD per 1 USD. */
  rate: number;
  /** ECB reference date (YYYY-MM-DD), or null when falling back. */
  date: string | null;
  /** True when the rate came from the live API (possibly via cache), false = hardcoded fallback. */
  live: boolean;
}

interface CachedFx {
  rate: number;
  date: string;
  fetchedAt: number;
}

function readCache(): CachedFx | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedFx;
    if (!parsed || typeof parsed.rate !== 'number' || !Number.isFinite(parsed.rate) || parsed.rate <= 0) return null;
    if (Date.now() - (parsed.fetchedAt ?? 0) > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Synchronous best-known rate: cached live value if fresh, else the fallback. */
export function getFxNzdCached(): FxRate {
  const cached = readCache();
  if (cached) return { rate: cached.rate, date: cached.date, live: true };
  return { rate: DEFAULT_FX_NZD, date: null, live: false };
}

/** Fetch the live rate (cached for the session); resolves to the fallback on any failure. */
export async function fetchFxNzd(): Promise<FxRate> {
  const cached = readCache();
  if (cached) return { rate: cached.rate, date: cached.date, live: true };
  try {
    const res = await fetch('https://api.frankfurter.dev/v1/latest?base=USD&symbols=NZD');
    if (!res.ok) throw new Error(`FX fetch failed: ${res.status}`);
    const body = (await res.json()) as { date?: string; rates?: { NZD?: number } };
    const rate = Number(body.rates?.NZD);
    if (!Number.isFinite(rate) || rate <= 0) throw new Error('FX fetch: no NZD rate in response');
    const entry: CachedFx = { rate, date: String(body.date ?? ''), fetchedAt: Date.now() };
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    } catch {
      // storage disabled/full — in-memory callers still get the live value this call
    }
    return { rate, date: entry.date, live: true };
  } catch {
    return { rate: DEFAULT_FX_NZD, date: null, live: false };
  }
}
